import {AgGridReact} from "ag-grid-react";
import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-quartz.css";
import {
    ColDef,
    GridApi,
    SizeColumnsToContentStrategy,
} from "ag-grid-community";
import {organizationStore} from "../../store/organization";
import {createApolloClient} from "../../providers/ApolloClientFactory";
import {GET_MENUS} from "./queries";
import {
    Link,
    LoaderFunctionArgs,
    useLoaderData,
    useNavigate,
    useRevalidator,
} from "react-router-dom";
import {LocationMenu, Menu} from "../../types/menu";
import {Badge} from "../../components/Badge";
import {useCallback, useEffect, useMemo, useRef, useState} from "react";
import {eventStore} from "../../store/event";
import moment from "moment/moment";
import {usePermissions} from "../auth/api";
import {PrimaryButton, SecondaryButton} from "../../components/Button";
import {Input} from "../../components/inputs/Input";
import {userStore} from "../../store/user";
import ImportCSVModal from "../../components/importCsv/MenuImportCSVModal";
import Switch from "react-switch";

import {useGridEdit} from "../helpers/GridEditHelper";
import TextSuggestEditor from "../helpers/TextSuggestEditorHelper";
import GridEditActions from "../helpers/GridEditActions";
import {UPDATE_MENU} from "./mutations";
import {GET_LIST_EVENTS_BY_ORGANIZATION, GET_LIST_VENDORS_BY_ORGANIZATION} from "../../queries";
import {useGridSave} from "../helpers/UseGridSave";

const client = createApolloClient();
const {getState} = userStore;
const {getState: getOrganizationState} = organizationStore;
const {getState: getEventStore} = eventStore;

const buildSearch = (search: string) => [
    {name: {_ilike: `%${search}%`}},
    {event: {name: {_ilike: `%${search}%`}}},
    {vendor: {name: {_ilike: `%${search}%`}}},
    {location_menus: {location: {name: {_ilike: `%${search}%`}}}},
];

type CellRenderer = (params: {
    value:
        | string
        | number
        | boolean
        | React.ReactElement<any, string | React.JSXElementConstructor<any>>
        | React.ReactFragment
        | React.ReactPortal
        | null
        | undefined;
    data: Menu;
}) => JSX.Element;

const createCellRenderer = (
    permission: boolean,
    linkFunc: (params: { data: Menu }) => string
): CellRenderer => {
    return (params) => (
        <>
            {permission ? (
                <Link to={linkFunc(params)}>
                    <Badge>{params.value}</Badge>
                </Link>
            ) : (
                <Badge>{params.value}</Badge>
            )}
        </>
    );
};

const createMultipleLinksCellRenderer = (
    permission: boolean,
    linkFunc: (locationMenu: LocationMenu) => string
): CellRenderer => {
    return (params) => {
        const locations = params.data?.location_menus?.map((lm, index) => (
            <span key={index}>
        {permission ? (
            <Link to={linkFunc(lm)}>
                <Badge>{lm.location.name}</Badge>
            </Link>
        ) : (
            <Badge>{lm.location.name}</Badge>
        )}
                {index < (params?.data?.location_menus?.length ?? 0) - 1 && " "}
      </span>
        ));
        return <>{locations}</>;
    };
};

export const menusListLoader = async ({ request }: LoaderFunctionArgs) => {
    const url = new URL(request.url);
    const search = url.searchParams.get("search");
    const rawOrganizationId = getOrganizationState().organizationId;
    const organizationId = parseInt(rawOrganizationId !== null ? rawOrganizationId.toString() : "", 10);
    const eventId = getEventStore().eventId;
    const user = getState().user;

    let where: any = { _or: search ? buildSearch(search) : undefined };
    if (organizationId && organizationId !== 0) where = { ...where, event: { organization_id: { _eq: organizationId } } };
    if (eventId) where = { ...where, event_id: { _eq: eventId } };
    else if (user?.events) where = { ...where, event_id: { _in: user?.events } };
    if (user?.vendors) where = { ...where, vendor_id: { _in: user?.vendors } };

    let eventsWhere: any = {};
    let vendorsWhere: any = {};
    if (organizationId && organizationId !== 0) {
        eventsWhere.organization_id = { _eq: organizationId };
        eventsWhere.is_active = { _eq: true };
        vendorsWhere.organization_id = { _eq: organizationId };
        vendorsWhere.is_active = { _eq: true };
    }
    if (user?.events?.length) eventsWhere.id = { _in: user.events };
    if (user?.vendors?.length) vendorsWhere.id = { _in: user.vendors };

    const [{ data: menusRes }, { data: eventsRes }, { data: vendorsRes }] = await Promise.all([
        client.query({ query: GET_MENUS, variables: { where } }),
        client.query({ query: GET_LIST_EVENTS_BY_ORGANIZATION, variables: { where: eventsWhere } }),
        client.query({ query: GET_LIST_VENDORS_BY_ORGANIZATION, variables: { where: vendorsWhere } }),
    ]);

    return {
        menus: menusRes.menus as Menu[],
        events: eventsRes.events,
        vendors: vendorsRes.vendors,
    };
};

export const MenuList = () => {
    const {editCreatePermission} = usePermissions("menus");
    const {editCreatePermission: eventsEditPermission} = usePermissions("events");
    const {editCreatePermission: vendorsEditPermission} = usePermissions("vendors");
    const {editCreatePermission: locationsEditPermission} = usePermissions("locations");

    const navigate = useNavigate();
    const revalidator = useRevalidator();
    const gridRef = useRef<GridApi>();
    const originalActiveRef = useRef<Map<string, boolean>>(new Map());

    const { menus, events, vendors } = useLoaderData() as {
        menus: Menu[];
        events: { id: number; name: string }[];
        vendors: { id: number; name: string }[];
    };

    const [rowData, setRowData] = useState<Menu[]>([]);
    const [isActiveToggle, setIsActiveToggle] = useState(true);

    const {pending, pendingCount, resetBaseline, makeEditable, makeStringToggle, setPending, getBaselineRows} = useGridEdit<Menu>();
    const getRowId = useCallback((p: { data: Menu }) => String(p.data.id), []);

    const getPatchedValue = (p: any, field?: string) => {
        if (!field) return p.value;
        const id = String(p.data?.id ?? "");
        const patch = p.context?.pending?.[id];
        if (patch && Object.prototype.hasOwnProperty.call(patch, field)) {
            return (patch as any)[field];
        }
        return p.data?.[field];
    };

    useEffect(() => {
        resetBaseline(menus);
        originalActiveRef.current = new Map(
            (menus ?? []).map(x => [String(x.id), !!x.is_active])
        );
        setRowData(menus);
    }, [menus, resetBaseline]);

    useEffect(() => {
        const api = gridRef.current;
        if (!api) return;
        const model = isActiveToggle
            ? { is_active: { filterType: 'text', type: 'equals', filter: 'Yes' } }
            : null;
        api.setFilterModel(model);
        api.onFilterChanged();
    }, [isActiveToggle, pendingCount]);


    useEffect(() => {
        gridRef.current?.refreshCells({force: true});
        gridRef.current?.refreshClientSideRowModel("filter");
    }, [pending]);

    const eventIdToName = useMemo(() => new Map(events.map(e => [Number(e.id), String(e.name ?? "").trim()])), [events]);
    const eventNameToId = useMemo(() => new Map(Array.from(eventIdToName, ([id, name]) => [name, id])), [eventIdToName]);
    const eventOptions  = useMemo(() => Array.from(eventNameToId.keys()).sort((a,b)=>a.localeCompare(b)), [eventNameToId]);

    const vendorIdToName = useMemo(() => new Map(vendors.map(v => [Number(v.id), String(v.name ?? "").trim()])), [vendors]);
    const vendorNameToId = useMemo(() => new Map(Array.from(vendorIdToName, ([id, name]) => [name, id])), [vendorIdToName]);
    const vendorOptions  = useMemo(() => Array.from(vendorNameToId.keys()).sort((a,b)=>a.localeCompare(b)), [vendorNameToId]);

    const menuLinkRenderer = useMemo(
        () => createCellRenderer(!!editCreatePermission, (p) => `/menus/${p.data.id}/edit`),
        [editCreatePermission]
    );
    const eventLinkRenderer = useMemo(
        () => createCellRenderer(!!eventsEditPermission, (p) => `/events/${p.data.event_id}/edit`),
        [eventsEditPermission]
    );
    const vendorLinkRenderer = useMemo(
        () => createCellRenderer(!!vendorsEditPermission, (p) => `/vendors/${p.data.vendor_id}/edit`),
        [vendorsEditPermission]
    );
    const locationsRenderer = useMemo(
        () =>
            createMultipleLinksCellRenderer(
                !!locationsEditPermission,
                (lm) => `/locations/${lm.location.id}/edit`
            ),
        [locationsEditPermission]
    );

    const colDefs: ColDef<Menu>[] = useMemo(() => {
        return [
            {
                headerName: "Id",
                valueGetter: (p) => p.data?.id,
                cellRenderer: menuLinkRenderer,
                filter: "agTextColumnFilter",
                pinned: "left",
            },
            {
                headerName: "Menu Name",
                field: "name",
                cellEditor: "agTextCellEditor",
                ...makeEditable(
                    "name",
                    {
                        type: "text",
                        canEdit: !!editCreatePermission,
                        formatter: (v) => String(v ?? "").trim(),
                        parser: (nv) => String(nv ?? "").trim(),
                        equals: (a, b) => String(a ?? "").trim() === String(b ?? "").trim(),
                    },
                    setRowData
                ),
                filter: "agTextColumnFilter",
                pinned: "left",
            },
            {
                headerName: "Is Active?",
                field: "is_active",
                cellDataType: "text",
                sortable: true,
                filter: "agTextColumnFilter",
                ...makeEditable(
                    "is_active",
                    {type: "boolean", canEdit: !!editCreatePermission, toggleOnClick: true},
                    setRowData
                ),

                valueFormatter: (p) => (getPatchedValue(p, "is_active") ? "Yes" : "No"),

                filterValueGetter: (p: any) => {
                    const id = String(p.data?.id ?? "");
                    const patch = p.context?.pending?.[id] as { is_active?: boolean } | undefined;

                    if (patch && Object.prototype.hasOwnProperty.call(patch, "is_active")) {
                        return patch.is_active ? "Yes" : "No";
                    }

                    const baseline = originalActiveRef.current.get(id);
                    return baseline ? "Yes" : "No";
                },
            },
            {
                headerName: "Event Name",
                field: "event_id",
                cellEditor: TextSuggestEditor,
                cellEditorParams: (p: { value: any; data: Menu }) => {
                    const id = Number(p.value ?? p.data?.event_id ?? p.data?.event?.id);
                    const initial = eventIdToName.get(id) ?? String(p.data?.event?.name ?? "");
                    return {
                        values: eventOptions,
                        showAllOnEdit: true,
                        initialValue: initial,
                    };
                },
                ...makeEditable(
                    "event_id" as any,
                    {
                        type: "text",
                        canEdit: !!editCreatePermission,
                        parser: (nv: any, ov: any) => {
                            const s = String(nv ?? "").trim();
                            if (!s) return ov;
                            const byName = eventNameToId.get(s);
                            if (byName != null) return byName;
                            const n = Number(s);
                            return Number.isFinite(n) && eventIdToName.has(n) ? n : ov;
                        },
                        equals: (a: any, b: any) => Number(a ?? 0) === Number(b ?? 0),
                    },
                    setRowData
                ),
                cellRenderer: (p: { value: any; data: Menu }) => {
                    const id = Number(p.value ?? p.data?.event_id ?? p.data?.event?.id);
                    const name = eventIdToName.get(id) ?? p.data?.event?.name ?? "";
                    if (!name) return null;
                    return eventsEditPermission ? (
                        <Link to={`/events/${id}/edit`} onMouseDown={(e) => e.stopPropagation()}>
                            <Badge>{name}</Badge>
                        </Link>
                    ) : (
                        <Badge>{name}</Badge>
                    );
                },
                filter: "agTextColumnFilter",
                filterValueGetter: (p) => {
                    const id = Number((p.data as Menu).event_id ?? (p.data as any)?.event?.id);
                    return eventIdToName.get(id) ?? (p.data as any)?.event?.name ?? "";
                },
            },
            {
                headerName: "Vendor Name",
                field: "vendor_id",
                cellEditor: TextSuggestEditor,
                cellEditorParams: (p: { value: any; data: Menu }) => {
                    const id = Number(p.value ?? p.data?.vendor_id ?? p.data?.vendor?.id);
                    const initial = vendorIdToName.get(id) ?? String(p.data?.vendor?.name ?? "");
                    return {
                        values: vendorOptions,
                        showAllOnEdit: true,
                        initialValue: initial,
                    };
                },
                ...makeEditable(
                    "vendor_id" as any,
                    {
                        type: "text",
                        canEdit: !!editCreatePermission,
                        parser: (nv: any, ov: any) => {
                            const s = String(nv ?? "").trim();
                            if (!s) return ov;
                            const byName = vendorNameToId.get(s);
                            if (byName != null) return byName;
                            const n = Number(s);
                            return Number.isFinite(n) && vendorIdToName.has(n) ? n : ov;
                        },
                        equals: (a: any, b: any) => Number(a ?? 0) === Number(b ?? 0),
                    },
                    setRowData
                ),
                cellRenderer: (p: { value: any; data: Menu }) => {
                    const id = Number(p.value ?? p.data?.vendor_id ?? p.data?.vendor?.id);
                    const name = vendorIdToName.get(id) ?? p.data?.vendor?.name ?? "";
                    if (!name) return null;
                    return vendorsEditPermission ? (
                        <Link to={`/vendors/${id}/edit`} onMouseDown={(e) => e.stopPropagation()}>
                            <Badge>{name}</Badge>
                        </Link>
                    ) : (
                        <Badge>{name}</Badge>
                    );
                },
                filter: "agTextColumnFilter",
                filterValueGetter: (p) => {
                    const id = Number((p.data as Menu).vendor_id ?? (p.data as any)?.vendor?.id);
                    return vendorIdToName.get(id) ?? (p.data as any)?.vendor?.name ?? "";
                },
            },
            {
                headerName: "Location(s) Name",
                valueGetter: (p) => p.data?.location_menus,
                valueFormatter: (params) => {
                    const locs: LocationMenu[] = params.value || [];
                    return locs.map((l) => l.location.name).join(", ");
                },
                cellRenderer: locationsRenderer,
                filter: "agTextColumnFilter",
                filterValueGetter: (p) => {
                    const locs: LocationMenu[] = (p.data as Menu)?.location_menus ?? [];
                    return locs.map((l) => l?.location?.name ?? "").filter(Boolean).join(", ");
                },
            },
            {
                headerName: "Tax Type",
                field: "tax_type",
                ...makeStringToggle(
                    "tax_type" as any,
                    {
                        canEdit: !!editCreatePermission,
                        trueValue: "inclusive",
                        falseValue: "exclusive",
                        labels: { on: "Inclusive", off: "Exclusive" },
                        toggleOnClick: true,
                    },
                    setRowData
                ),
                filter: "agTextColumnFilter",
                sortable: true,
            },
            ...(
                [
                    ["Cash?", "is_cash"],
                    ["Credit?", "is_credit"],
                    ["RFID?", "is_rfid"],
                    ["QR?", "is_qr"],
                    ["Tabs?", "is_tab"],
                    ["Tips?", "is_tips"],
                    ["Custom Items?", "is_custom_item"],
                    ["Discounts?", "is_discount"],
                    ["Discounts Protected?", "is_discount_protected"],
                ] as const
            ).map(([header, field]) => ({
                headerName: header,
                field,
                ...makeEditable(field as keyof Menu, {
                    type: "boolean",
                    canEdit: !!editCreatePermission,
                    toggleOnClick: true
                }, setRowData),
                valueFormatter: (p: any) => (p.value ? "Yes" : "No"),
                filter: "agTextColumnFilter",
                filterValueGetter: (p: any) => ((p.data as Menu)?.[field] ? "Yes" : "No"),
            })),
            {
                headerName: "Updated By",
                valueGetter: (p) => p.data?.user?.username,
            },
            {
                headerName: "Created Date",
                valueGetter: (p) => p.data?.created_at,
                valueFormatter: (p) => (p.value ? moment(p.value).format("MM/DD/YYYY h:mm:ss a") : ""),
                filter: "agTextColumnFilter",
                filterValueGetter: (p) =>
                    p.data?.created_at ? moment(p.data.created_at).format("MM/DD/YYYY h:mm:ss a") : "",
            },
            {
                headerName: "Updated Date",
                valueGetter: (p) => p.data?.updated_at,
                valueFormatter: (p) => (p.value ? moment(p.value).format("MM/DD/YYYY h:mm:ss a") : ""),
                filter: "agTextColumnFilter",
                filterValueGetter: (p) =>
                    p.data?.updated_at ? moment(p.data.updated_at).format("MM/DD/YYYY h:mm:ss a") : "",
            },
        ] as ColDef<Menu>[];
    }, [
        editCreatePermission,
        eventsEditPermission,
        vendorsEditPermission,
        locationsEditPermission,
        makeEditable,
        setRowData,
        menuLinkRenderer,
        eventLinkRenderer,
        vendorLinkRenderer,
        locationsRenderer,
        eventIdToName, eventNameToId, eventOptions,
        vendorIdToName, vendorNameToId, vendorOptions,
        makeStringToggle,
    ]);

    const defaultColDef = useMemo(() => ({
        valueGetter: (p: any) => p.colDef.field ? getPatchedValue(p, p.colDef.field) : p.value,
        // RP-2583: columns stay put. Dragging a header (easy to do by accident on a phone)
        // used to reorder columns or pull them off the grid.
        suppressMovable: true,
    }), []);

    const rowHeight = 50;
    const autoSizeStrategy: SizeColumnsToContentStrategy = {type: "fitCellContents"};
    const gridOptions = useMemo(() => ({enableCellTextSelection: true, context: {pending}}), [pending]);

    const handleExportCSV = () => {
        if (gridRef.current) {
            const timestamp = new Date().toISOString().replace(/[-:]/g, "");
            gridRef.current.exportDataAsCsv({fileName: `Menus_${timestamp}.csv`, columnSeparator: ","});
        }
    };

    const mutateRow = async (id: number, patch: Partial<Menu>) => {
        const user = getState().user;
        const input = {
            ...patch,
            last_updated_by: user?.id,
            updated_at: new Date().toISOString()
        };
        const res = await client.mutate({
            mutation: UPDATE_MENU,
            variables: { id, input },
            errorPolicy: "all",
        });
        if (res.errors?.length) throw new Error(res.errors.map((e) => e.message).join("; "));
        const updatedId = (res.data as any)?.update_menus_by_pk?.id;
        if (!updatedId) throw new Error(`No id returned for ${id}`);
    };

    const { handleSave } = useGridSave<Menu>({
        rowData,
        setRowData,
        pending,
        setPending,
        resetBaseline,
        gridApiRef: gridRef as any,
        revalidate: () => revalidator.revalidate(),
        mutateRow,
        optimistic: true,
        baselines: [
            { ref: originalActiveRef, value: (r) => !!r.is_active },
        ],
    });

    return (
        <div className="w-full flex flex-col overflow-hidden" style={{ height: "calc(100vh - 128px)" }}>
            <div className="md:hidden grid grid-cols-6 min-w-0 px-0 py-0">
                <div className="col-span-6 flex items-center justify-between min-h-[48px] mb-1">
                    <div className="flex items-center gap-2 pl-2 pt-2">
                        <GridEditActions
                            pendingCount={pendingCount}
                            pending={pending}
                            gridRef={gridRef}
                            resetBaseline={resetBaseline}
                            baselineData={menus}
                            getBaselineRows={getBaselineRows}
                            isActiveToggle={isActiveToggle}
                            setRowData={setRowData}
                            setPending={setPending}
                            onSave={handleSave}
                        />
                    </div>

                    <div className="ml-2 flex flex-col items-center">
                        <span className="block text-xs font-medium text-gray-500 whitespace-nowrap">Active Only</span>
                        <Switch
                            checked={isActiveToggle}
                            onChange={() => setIsActiveToggle((prev) => !prev)}
                            onColor="#EE2E30"
                            onHandleColor="#FFFFFF"
                            handleDiameter={18}
                            uncheckedIcon={false}
                            checkedIcon={false}
                            boxShadow="0px 1px 5px rgba(0, 0, 0, 0.6)"
                            activeBoxShadow="0px 0px 1px 10px rgba(0, 0, 0, 0.2)"
                            height={15}
                            width={48}
                        />
                    </div>
                </div>

                <div className="col-span-6 flex items-center gap-2">
                    <div className="flex-1 min-w-0">
                        <div className="w-full px-3 py-2">
                            <Input
                                name="search"
                                placeholder="Search Menus"
                                onChange={(s) => {
                                    navigate(s && typeof s === "string" && s.length > 0 ? `/menus/list?search=${s}` : `/menus/list`);
                                    revalidator.revalidate();
                                }}
                            />
                        </div>
                    </div>

                    <div className="flex items-center gap-1 shrink-0">
                        <SecondaryButton onClick={handleExportCSV}>Export to CSV</SecondaryButton>
                        {editCreatePermission && (
                            <PrimaryButton onClick={() => navigate(`/menus/create`)}>Create</PrimaryButton>
                        )}
                    </div>
                </div>
            </div>

            <div className="hidden md:grid grid-cols-12 items-center gap-3 min-w-0 py-2">
                <div className="col-span-5 min-w-0">
                    <div className="w-full">
                        <Input
                            name="search"
                            placeholder="Search Menus"
                            onChange={(s) => {
                                navigate(s && typeof s === "string" && s.length > 0 ? `/menus/list?search=${s}` : `/menus/list`);
                                revalidator.revalidate();
                            }}
                        />
                    </div>
                </div>

                <div className="col-span-3 col-start-6 justify-self-start flex items-center gap-1">
                    <GridEditActions
                        pendingCount={pendingCount}
                        pending={pending}
                        gridRef={gridRef}
                        resetBaseline={resetBaseline}
                        baselineData={menus}
                        getBaselineRows={getBaselineRows}
                        isActiveToggle={isActiveToggle}
                        setRowData={setRowData}
                        setPending={setPending}
                        onSave={handleSave}
                    />
                </div>

                <div className="col-span-1 flex flex-col items-center">
                    <span className="block text-sm font-medium text-gray-500 whitespace-nowrap">Active Only</span>
                    <Switch
                        checked={isActiveToggle}
                        onChange={() => setIsActiveToggle((prev) => !prev)}
                        onColor="#EE2E30"
                        onHandleColor="#FFFFFF"
                        handleDiameter={18}
                        uncheckedIcon={false}
                        checkedIcon={false}
                        boxShadow="0px 1px 5px rgba(0, 0, 0, 0.6)"
                        activeBoxShadow="0px 0px 1px 10px rgba(0, 0, 0, 0.2)"
                        height={15}
                        width={48}
                    />
                </div>

                <div className="col-span-3 flex justify-end items-center gap-1 whitespace-nowrap">
                    <SecondaryButton onClick={handleExportCSV}>
                        <span className="whitespace-nowrap">Export&nbsp;to&nbsp;CSV</span>
                    </SecondaryButton>
                    {editCreatePermission && (
                        <PrimaryButton onClick={() => navigate(`/menus/create`)}>Create</PrimaryButton>
                    )}
                </div>
            </div>

            <div className="flex-1 min-h-0">
                <div className="ag-theme-quartz w-full h-full min-w-0">
                    <AgGridReact
                        rowData={rowData}
                        columnDefs={colDefs}
                        defaultColDef={defaultColDef}
                        suppressDragLeaveHidesColumns
                        rowHeight={rowHeight}
                        autoSizeStrategy={autoSizeStrategy}
                        pagination
                        gridOptions={gridOptions}
                        paginationPageSize={100}
                        getRowId={getRowId}
                        onGridReady={(params) => {
                            gridRef.current = params.api;
                            if (isActiveToggle) {
                                const model = { is_active: { filterType: 'text', type: 'equals', filter: 'Yes' } };
                                params.api.setFilterModel(model);
                                params.api.onFilterChanged();
                            }
                        }}
                        singleClickEdit
                        stopEditingWhenCellsLoseFocus
                    />
                </div>
            </div>
        </div>
    );
};
